import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import {
  FileWarning, HelpCircle, Calculator, Shield, Clock, Users,
  ArrowRight, CheckCircle, Phone, ChevronRight, Search, FileText, Briefcase
} from 'lucide-react';

const taxQuestions = [
  { icon: FileWarning, question: 'What do I do about this IRS notice?' },
  { icon: HelpCircle, question: 'Can I write this off?' },
  { icon: Calculator, question: 'Why do I owe so much?' },
  { icon: FileText, question: 'What happens if I have unfiled returns?' },
  { icon: Shield, question: 'Am I in trouble with the IRS?' },
  { icon: Clock, question: 'Is it too late to fix this?' },
];

const steps = [
  {
    number: '01',
    title: 'Ask Your Tax Question',
    description: 'Start with a simple question, IRS notice, or tax concern. No commitment required.',
    icon: Search,
  },
  {
    number: '02',
    title: 'Get a Clear Review',
    description: 'We explain the issue, discuss likely options, and identify the right next steps.',
    icon: FileText,
  },
  {
    number: '03',
    title: 'Move Forward With Confidence',
    description: 'If needed, we help with tax preparation, resolution, or ongoing advisory support.',
    icon: CheckCircle,
  },
];

const featuredServices = [
  {
    title: 'IRS Notice Review',
    description: 'Got a letter from the IRS? We review it, explain what it means, and help you respond before deadlines become costly.',
    path: '/services/irs-notice',
    icon: FileWarning,
  },
  {
    title: 'Tax Diagnostic Review',
    description: 'A deeper review of your tax situation. We examine your documents, explain options, and recommend next steps.',
    path: '/services/diagnostic',
    icon: Search,
  },
  {
    title: 'Tax Resolution Services',
    description: 'Payment plans, penalty relief, unfiled returns, and collections help. Full-service support when you need it.',
    path: '/services/resolution',
    icon: Shield,
  },
  {
    title: 'Tax Preparation',
    description: 'Individual, self-employed, small business, prior-year, and amended returns prepared by experienced professionals.',
    path: '/services/preparation',
    icon: Calculator,
  },
  {
    title: 'Small Business Tax Help',
    description: 'LLC and S-corp guidance, estimated taxes, deduction questions, and year-round support for business owners.',
    path: '/services/small-business',
    icon: Briefcase,
  },
];

const credibilityPoints = [
  '25+ years of tax experience',
  'Thousands of tax issues resolved',
  'Individual and business clients',
  'Plain-English guidance, no hype',
  'Confidential, professional approach',
  'No unrealistic promises',
];

const testimonials = [
  {
    quote: 'I received an IRS notice and had no idea what to do. TaxPro - Answers walked me through the entire process and resolved it quickly.',
    name: 'Michael R.',
    role: 'Small Business Owner',
  },
  {
    quote: 'After years of unfiled returns, I was terrified. They made it simple, explained my options, and helped me get back on track.',
    name: 'Sarah K.',
    role: 'Individual Taxpayer',
  },
  {
    quote: 'Clear, honest, and professional. No scare tactics, no upselling. Just solid advice and real help with my tax problem.',
    name: 'David L.',
    role: 'Self-Employed Professional',
  },
];

export default function HomePage() {
  useSEO({
    title: 'Clear Answers to Your Tax Questions',
    description: 'With 25 years of tax experience, TaxPro - Answers helps individuals and business owners understand their tax problems, review options, and take the next right step.',
  });

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative bg-brand-navy overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1772290137750-101578a38881?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMGJsdWUlMjBncmVlbnxlbnwwfHx8fDE3NzYzMDI4MzF8MA&ixlib=rb-4.1.0&q=85)`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <AnimatedSection className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-6">
              Trusted Tax Guidance for 25+ Years
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-white mb-6">
              Clear Answers to Your Tax Questions
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-4 max-w-2xl">
              With 25 years of tax experience, we help individuals and business owners understand their tax problems, review their options, and take the next right step.
            </p>
            <p className="text-base text-slate-400 mb-10">
              Start with a simple tax question, an IRS notice review, or a paid diagnostic consultation.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/free-consultation">
                <Button
                  data-testid="hero-cta-book-free"
                  className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
                >
                  Book Your Free Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/ask">
                <Button
                  data-testid="hero-cta-ask"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold px-7 py-6 text-base rounded-md"
                >
                  Ask a Tax Question - Free
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Common Tax Questions */}
      <section data-testid="tax-questions-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Sound Familiar?
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4 max-w-xl">
              Get clear answers before small problems become expensive ones
            </h2>
            <p className="text-base text-slate-500 mb-12 max-w-xl">
              Many people don't begin by asking for full tax preparation. They begin with a question.
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {taxQuestions.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="flex items-start gap-4 p-6 border border-slate-200 rounded-lg hover:border-brand-green/40 transition-colors group">
                  <div className="w-10 h-10 rounded-md bg-brand-navy/5 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-green/10 transition-colors">
                    <item.icon className="w-5 h-5 text-brand-navy group-hover:text-brand-green transition-colors" />
                  </div>
                  <p className="text-sm font-medium text-slate-700 leading-relaxed pt-2">
                    "{item.question}"
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection delay={0.3} className="mt-10">
            <Link to="/ask" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-green hover:text-brand-green-hover transition-colors">
              Ask Your Question Now <ChevronRight className="w-4 h-4" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* 3-Step Process */}
      <section data-testid="process-section" className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              How It Works
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-14 max-w-lg">
              Three simple steps to resolve your tax concerns
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.12}>
                <div className="relative">
                  <span className="font-heading font-bold text-6xl text-brand-navy/5 absolute -top-4 -left-2">
                    {step.number}
                  </span>
                  <div className="relative pt-8">
                    <div className="w-12 h-12 rounded-lg bg-brand-navy flex items-center justify-center mb-5">
                      <step.icon className="w-6 h-6 text-brand-green" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-brand-navy mb-3">
                      {step.title}
                    </h3>
                    <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section data-testid="services-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Our Services
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-14 max-w-lg">
              Tax help at every level, from a quick question to full resolution
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
            {featuredServices.map((service, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <Link
                  to={service.path}
                  data-testid={`service-card-${service.path.split('/').pop()}`}
                  className="block bg-white p-8 hover:bg-brand-muted transition-colors group h-full"
                >
                  <div className="w-11 h-11 rounded-md bg-brand-navy/5 flex items-center justify-center mb-5 group-hover:bg-brand-green/10 transition-colors">
                    <service.icon className="w-5 h-5 text-brand-navy group-hover:text-brand-green transition-colors" />
                  </div>
                  <h3 className="font-heading font-semibold text-base text-brand-navy mb-3 group-hover:text-brand-green transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-green opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ChevronRight className="w-3 h-3" />
                  </span>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Credibility */}
      <section data-testid="credibility-section" className="py-20 lg:py-28 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
                Why Work With Us
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-white mb-6 max-w-md">
                25 years of solving real tax problems for real people
              </h2>
              <p className="text-base text-slate-400 leading-relaxed mb-8 max-w-md">
                We provide practical, plain-English guidance. No hype, no unrealistic promises. Just professional, confidential help with your tax situation.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {credibilityPoints.map((point, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0" />
                    <span className="text-sm text-slate-300">{point}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/3866394/pexels-photo-3866394.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Diverse team of professionals in consultation"
                  className="w-full h-80 lg:h-96 object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-brand-navy/20" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Client Experiences
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-14 max-w-lg">
              Real results from real clients
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="bg-white border border-slate-200 rounded-lg p-8 h-full flex flex-col">
                  <div className="flex gap-1 mb-5">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="w-4 h-4 bg-brand-green rounded-full" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-6 flex-1">
                    "{t.quote}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-brand-navy">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.role}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="final-cta-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
              Ready to get clear answers?
            </h2>
            <p className="text-base text-slate-500 mb-8 max-w-lg mx-auto">
              Start with a simple question. No commitment, no pressure. Just honest, experienced tax guidance.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/ask">
                <Button
                  data-testid="final-cta-ask"
                  className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-7 py-6 text-base rounded-md"
                >
                  Ask a Tax Question - Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="final-cta-book"
                  variant="outline"
                  className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-7 py-6 text-base rounded-md"
                >
                  Book a Consultation
                </Button>
              </a>
            </div>
            <a href="tel:8555892461" className="inline-flex items-center gap-2 mt-6 text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] hover:text-brand-navy transition-colors">
              <Phone className="w-4 h-4" /> Or call us: 855-589-2461
            </a>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
